import React, { useEffect, useRef } from 'react';
import MessageBubble from './MessageBubble';
import ToolRequestCard from './ToolRequestCard';
import ChatInput, { FileUploadState, MentionItem } from './ChatInput';
import { Message, ThinkingMode } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import { InstalledSkill } from '../services/workflowApi';

interface ChatAreaProps {
  messages: Message[];
  onSendMessage: (message: string, pendingFiles?: File[], thinkingMode?: ThinkingMode, mentions?: MentionItem[], isToolResponse?: boolean, forcedSkillId?: string) => void;
  isLoading: boolean;
  currentSessionId: string | null;
  currentLanguage: string;
  fileUploadState?: FileUploadState | null;
  onLegalDrafterOpen?: () => void;
  onCancelRequest?: () => void;

  // 受控输入模式 - 用于状态持久化
  inputMessage?: string;
  onInputMessageChange?: (message: string) => void;

  // @ 提及文件列表
  mentionFiles?: MentionItem[];

  // HITL 授权回调
  onToolApprove?: (messageId: string) => void;
  onToolReject?: (messageId: string) => void;

  // 🧩 Architecture 3.0: 已安装的 SubAgent 技能列表
  installedSkills?: InstalledSkill[];
}

const ChatArea: React.FC<ChatAreaProps> = ({
  messages,
  onSendMessage,
  isLoading,
  currentSessionId,
  currentLanguage,
  fileUploadState,
  onLegalDrafterOpen,
  onCancelRequest,

  inputMessage,
  onInputMessageChange,

  mentionFiles,

  onToolApprove,
  onToolReject,

  installedSkills,
}) => {
  const { t } = useTranslation(currentLanguage);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="flex-1 flex flex-col h-full bg-chat-bg">
      {currentSessionId ? (
        messages.length === 0 ? (
          /* 初始状态：输入框在中央 */
          <div className="flex-1 flex flex-col items-center justify-center -mt-20">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold text-gray-800 mb-6">
                {t('lawhelperAi')}
              </h2>
            </div>
            <div className="w-full max-w-2xl px-4">
              <ChatInput
                onSendMessage={onSendMessage}
                isLoading={isLoading}
                onCancelRequest={onCancelRequest}
                placeholder={isLoading ? "Waiting for response..." : t('askAnything')}
                currentLanguage={currentLanguage}
                onLegalDrafterOpen={onLegalDrafterOpen}

                message={inputMessage}
                onMessageChange={onInputMessageChange}
                mentionFiles={mentionFiles}
                installedSkills={installedSkills}
              />
            </div>
          </div>
        ) : (
          /* 对话状态：正常布局 */
          <>
            {/* 消息列表 */}
            <div className="flex-1 overflow-y-auto">
              <div>
                {messages.map((message, index) => {
                  // 查找上一条用户消息（用于判断是否简单对话）
                  let previousUserContent: string | undefined;
                  if (message.role === 'ai') {
                    for (let i = index - 1; i >= 0; i--) {
                      if (messages[i].role === 'user') {
                        previousUserContent = messages[i].content;
                        break;
                      }
                    }
                  }
                  return (
                    <React.Fragment key={message.message_id}>
                    <MessageBubble
                      key={message.message_id}
                      message={message}
                      isStreaming={!message.is_final}
                      currentLanguage={currentLanguage}
                      previousUserContent={previousUserContent}
                    />
                    {/* HITL: 如果这条 AI 消息带有工具请求，在气泡下方渲染授权卡片 */}
                    {message.toolRequest && (
                      <div className="flex px-4 md:px-16 lg:px-24 mb-2">
                        <div className="max-w-3xl w-full">
                          <ToolRequestCard
                            toolRequest={message.toolRequest}
                            onApprove={() => onToolApprove?.(message.message_id)}
                            onReject={() => onToolReject?.(message.message_id)}
                          />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* 输入框在底部 */}
            <ChatInput
              onSendMessage={onSendMessage}
              isLoading={isLoading}
              onCancelRequest={onCancelRequest}
              placeholder={isLoading ? "Waiting for response..." : "Type a message..."}
              currentLanguage={currentLanguage}
              onLegalDrafterOpen={onLegalDrafterOpen}

              message={inputMessage}
              onMessageChange={onInputMessageChange}
              mentionFiles={mentionFiles}
              installedSkills={installedSkills}
            />
          </>
        )
      ) : (
        /* 未选择会话状态：显示输入框在中央 */
        <div className="flex-1 flex flex-col items-center justify-center -mt-20">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-semibold text-gray-800 mb-6">
              {t('lawhelperAi')}
            </h2>
          </div>
          <div className="w-full max-w-2xl px-4">
            <ChatInput
              onSendMessage={onSendMessage}
              isLoading={isLoading}
              onCancelRequest={onCancelRequest}
              placeholder={isLoading ? "Waiting for response..." : t('askAnything')}
              currentLanguage={currentLanguage}
              onLegalDrafterOpen={onLegalDrafterOpen}

              message={inputMessage}
              onMessageChange={onInputMessageChange}
              mentionFiles={mentionFiles}
              installedSkills={installedSkills}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatArea; 